import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AnswerType, GameProgress, GameResult } from '@/types/aiOrHumanGame';
import { mockAIOrHumanGame } from '@/data/aiOrHumanGameMock';

const AIOrHumanGamePage: FC = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState<GameProgress>({
    currentRound: 1,
    totalRounds: mockAIOrHumanGame.rounds.length,
    stats: {
      correctAnswers: 0,
      wrongAnswers: 0,
      bonusMultiplier: 1,
      xpStreak: 120,
    },
  });

  const [showFeedback, setShowFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [gameResult, setGameResult] = useState<GameResult | null>(null);
  const [, setUserAnswers] = useState<Record<number, AnswerType>>({});

  const currentRound = mockAIOrHumanGame.rounds[progress.currentRound - 1];
  const progressPercentage = (progress.currentRound / progress.totalRounds) * 100;

  const handleAnswer = (answer: AnswerType) => {
    if (showFeedback || gameResult) return; // Previne múltiplos cliques

    const isCorrect = answer === currentRound.correctAnswer;
    
    // Salva a resposta do usuário
    setUserAnswers(prev => ({
      ...prev,
      [progress.currentRound]: answer
    }));

    // Atualiza estatísticas
    const newStats = {
      ...progress.stats,
      correctAnswers: isCorrect ? progress.stats.correctAnswers + 1 : progress.stats.correctAnswers,
      wrongAnswers: isCorrect ? progress.stats.wrongAnswers : progress.stats.wrongAnswers + 1,
      bonusMultiplier: isCorrect ? Math.min(progress.stats.bonusMultiplier + 0.5, 3) : 1,
    };

    setProgress(prev => ({
      ...prev,
      stats: newStats,
    }));

    // Mostra feedback visual
    setShowFeedback(isCorrect ? 'correct' : 'wrong');

    // Aguarda 1.5s antes de avançar ou mostrar resultado
    setTimeout(() => {
      setShowFeedback(null);

      if (progress.currentRound === progress.totalRounds) {
        // Fim do jogo - calcula resultado
        const score = Math.round((newStats.correctAnswers / progress.totalRounds) * 100);
        const baseXP = newStats.correctAnswers * mockAIOrHumanGame.baseXPPerRound;
        const bonusXP = Math.round(baseXP * (newStats.bonusMultiplier - 1));

        setGameResult({
          completed: true,
          totalRounds: progress.totalRounds,
          correctAnswers: newStats.correctAnswers,
          wrongAnswers: newStats.wrongAnswers,
          score,
          xpEarned: baseXP,
          bonusXP,
        });
      } else {
        // Avança para próxima rodada
        setProgress(prev => ({
          ...prev,
          currentRound: prev.currentRound + 1,
        }));
      }
    }, 1500);
  };

  const handleRestart = () => {
    setProgress({
      currentRound: 1,
      totalRounds: mockAIOrHumanGame.rounds.length,
      stats: {
        correctAnswers: 0,
        wrongAnswers: 0,
        bonusMultiplier: 1,
        xpStreak: 120,
      },
    });
    setShowFeedback(null);
    setGameResult(null);
    setUserAnswers({});
  };

  const handleExit = () => {
    navigate('/play');
  };

  // Tela de resultado final
  if (gameResult) {
    const passed = gameResult.score >= mockAIOrHumanGame.passingScore;

    return (
      <div className="min-h-screen bg-surface-primary flex flex-col">
        {/* Header */}
        <header className="w-full max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleExit}
              className="size-10 flex items-center justify-center rounded-full bg-surface-secondary hover:bg-surface-hover transition-all text-content-primary"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
            <div className="hidden md:flex flex-col">
              <h1 className="text-sm font-bold uppercase tracking-widest text-accent">Hub Play</h1>
              <p className="text-xs text-content-muted">IA ou Humano?</p>
            </div>
          </div>
        </header>

        {/* Result Content */}
        <main className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-2xl">
            <div className={`relative bg-surface-card border ${passed ? 'border-green-500' : 'border-red-500'} rounded-xl p-8 md:p-12 shadow-2xl`}>
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className={`size-24 rounded-full flex items-center justify-center ${passed ? 'bg-green-500' : 'bg-red-500'}`}>
                  <span className="material-symbols-outlined text-white text-5xl">
                    {passed ? 'check_circle' : 'cancel'}
                  </span>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-3xl font-bold text-center mb-4">
                {passed ? 'Parabéns! 🎉' : 'Não foi dessa vez...'}
              </h2>

              <p className="text-center text-content-secondary mb-8">
                {passed 
                  ? 'Você completou o desafio com sucesso!' 
                  : `Você precisa de ${mockAIOrHumanGame.passingScore}% de acerto para ganhar XP.`
                }
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-surface-secondary rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-500">{gameResult.correctAnswers}</p>
                  <p className="text-xs text-content-muted uppercase tracking-wider">Acertos</p>
                </div>
                <div className="bg-surface-secondary rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-500">{gameResult.wrongAnswers}</p>
                  <p className="text-xs text-content-muted uppercase tracking-wider">Erros</p>
                </div>
                <div className="bg-surface-secondary rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-accent">{gameResult.score}%</p>
                  <p className="text-xs text-content-muted uppercase tracking-wider">Pontuação</p>
                </div>
                <div className="bg-surface-secondary rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-accent">
                    {passed ? `+${gameResult.xpEarned + gameResult.bonusXP}` : '0'}
                  </p>
                  <p className="text-xs text-content-muted uppercase tracking-wider">XP Ganho</p>
                </div>
              </div>

              {/* Bonus Info */}
              {passed && gameResult.bonusXP > 0 && (
                <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-8 text-center">
                  <p className="text-sm text-accent font-bold">
                    🔥 Bônus de Streak: +{gameResult.bonusXP} XP
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={handleRestart}
                  className="flex-1 py-3 bg-surface-tertiary hover:bg-surface-hover rounded-full font-bold transition-all text-content-primary"
                >
                  Jogar Novamente
                </button>
                <button
                  onClick={handleExit}
                  className="flex-1 py-3 bg-accent text-accent-contrast rounded-full font-bold hover:brightness-110 transition-all"
                >
                  Voltar ao Hub
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Tela do jogo em andamento
  return (
    <div className="min-h-screen bg-surface-primary flex flex-col">
      {/* Feedback Overlay */}
      {showFeedback && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center ${showFeedback === 'correct' ? 'bg-green-500/10' : 'bg-red-500/10'} backdrop-blur-sm pointer-events-none border-[12px] ${showFeedback === 'correct' ? 'border-green-500/40' : 'border-red-500/40'}`}>
          <div className={`${showFeedback === 'correct' ? 'bg-green-500' : 'bg-red-500'} text-white p-6 rounded-full shadow-2xl scale-150`}>
            <span className="material-symbols-outlined text-6xl">
              {showFeedback === 'correct' ? 'check_circle' : 'cancel'}
            </span>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <header className="w-full max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleExit}
            className="size-10 flex items-center justify-center rounded-full bg-surface-secondary hover:bg-surface-hover transition-all text-content-primary"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
          <div className="hidden md:flex flex-col">
            <h1 className="text-sm font-bold uppercase tracking-widest text-accent">Hub Play</h1>
            <p className="text-xs text-content-muted">IA ou Humano?</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-accent/10 border border-accent/20 px-4 py-2 rounded-full shadow-lg shadow-accent/5">
            <span className="material-symbols-outlined text-accent text-xl">local_fire_department</span>
            <span className="text-sm font-bold text-accent tracking-tight">XP Streak: {progress.stats.xpStreak}</span>
          </div>
          <div className="size-10 rounded-full bg-surface-tertiary overflow-hidden border-2 border-accent/40">
            <img
              alt="Avatar do jogador"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuCAMcDBLV7xF7cqR0xKtaOsmw5YiBhLQklvffEaAAW-UNNgLJ6luqwNtuxffbc29BcHzlvk46yxvT8mKd637QwoqkX1j_1VVh3UWXDZghiByx4hWTTzPNhLmCDK_B3wuZhtEtX7PYTzVlYu-he4ZtY7ztHAPYSxuBqL-J8OGcnCw5xxLB0xwalhcesH2trsNIWZub1AvGzgrGsLTT2TS8Im1wKqyCrC5ivVNidFmP1-axPQ74We0yqINc77J4KmAlFMHKBqZJM4xJo"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-2xl space-y-8">
          {/* Progress Bar */}
          <div className="flex flex-col gap-3 w-full">
            <div className="flex justify-between items-end">
              <span className="text-xs font-bold uppercase tracking-widest text-content-muted">
                Progresso da Partida
              </span>
              <span className="text-sm font-bold text-accent">
                {progress.currentRound} / {progress.totalRounds}
              </span>
            </div>
            <div className="h-2.5 w-full bg-surface-tertiary rounded-full overflow-hidden">
              <div
                className="h-full bg-accent rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>

          {/* Central Content Card */}
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-accent/20 to-accent/20 rounded-xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
            <div className="relative bg-surface-card border border-theme-border rounded-xl p-8 md:p-12 shadow-2xl flex flex-col gap-6 overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                <span className="material-symbols-outlined text-9xl">format_quote</span>
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-xs font-bold text-accent uppercase tracking-widest">
                  Rodada {currentRound.roundNumber}
                </p>
                <h2 className="text-2xl font-bold leading-tight">Analise o texto abaixo:</h2>
              </div>

              <div className="min-h-[160px] flex items-center justify-center">
                <blockquote className="text-lg md:text-xl text-content-primary leading-relaxed font-medium text-center italic">
                  {currentRound.text}
                </blockquote>
              </div>

              <div className="h-px bg-surface-secondary w-full"></div>

              <p className="text-content-muted text-sm text-center font-medium italic">
                Dica: {currentRound.hint}
              </p>
            </div>
          </div>

          {/* Meta Text */}
          <div className="text-center space-y-2">
            <p className="text-content-secondary text-sm font-medium">
              Clique em uma das opções para validar seu palpite
            </p>
          </div>

          {/* Button Group */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            <button
              onClick={() => handleAnswer('human')}
              disabled={showFeedback !== null}
              className="flex items-center justify-center gap-3 px-8 py-5 bg-surface-tertiary hover:bg-surface-hover text-content-primary rounded-full transition-all duration-200 active:scale-95 group border-2 border-transparent hover:border-theme-border disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-2xl">person</span>
              <span className="text-lg font-bold">Humano</span>
            </button>
            <button
              onClick={() => handleAnswer('ai')}
              disabled={showFeedback !== null}
              className="flex items-center justify-center gap-3 px-8 py-5 bg-accent text-accent-contrast rounded-full transition-all duration-200 active:scale-95 shadow-lg shadow-accent/20 hover:brightness-110 border-2 border-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-2xl">smart_toy</span>
              <span className="text-lg font-bold">Inteligência Artificial</span>
            </button>
          </div>
        </div>
      </main>

      {/* Footer Stats */}
      <footer className="w-full max-w-5xl mx-auto px-6 py-8 flex justify-center md:justify-end">
        <div className="flex gap-8 text-content-muted text-xs font-bold uppercase tracking-widest">
          <div className="flex items-center gap-2">
            <span className="size-2 bg-green-500 rounded-full"></span>
            Acertos: {progress.stats.correctAnswers}
          </div>
          <div className="flex items-center gap-2">
            <span className="size-2 bg-red-500 rounded-full"></span>
            Erros: {progress.stats.wrongAnswers}
          </div>
          <div className="flex items-center gap-2 text-accent">
            <span className="size-2 bg-accent rounded-full animate-pulse"></span>
            Bônus: {progress.stats.bonusMultiplier}x
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AIOrHumanGamePage;
